import React, { useCallback, useState } from "react";
import { Calendar, momentLocalizer } from "react-big-calendar";
import moment from "moment";
import "./E3Calendar.css";
import PropTypes from "prop-types";

const localizer = momentLocalizer(moment);

/**
 * @typedef {Object} CalendarTask
 * @property {string|number} id - 작업 ID (unique)
 * @property {string} title - 작업 제목
 * @property {Date} start - 시작 날짜/시간
 * @property {Date} end - 종료 날짜/시간
 * @property {string} [allDay] - 하루 종일 여부
 * @property {any} [resource] - Payload
 * @property {"'green' | 'yellow' | 'red' | 'black'"} [color] - Task Background color
 */

/**
 * @typedef {Object} E3CalendarProps
 * @property {string|number} [height] - 캘린더 높이 (CSS 단위 또는 px 숫자)
 * @property {Date} [defaultToday] - 캘린더 기본 날짜
 * @property {(date: Date, info: {list: Date[], start: Date, end: Date}) => void} [onSelect] - 날짜 선택 콜백
 * @property {(task: CalendarTask, e: React.SyntheticEvent) => void} [onTaskSelect] - 작업 선택 콜백
 * @property {CalendarTask[]} [defaultTasks] - 초기 작업 목록
 * @property {(tasks: CalendarTask[], addedTask?: CalendarTask) => void} [onTaskChange] - 작업 변경 콜백
 * @property {React.ComponentType<DateHeaderProps> | undefined} [dayComponent] - 작업 변경 콜백
 */

/**
 * E3Calendar 컴포넌트
 * React Big Calendar 기반의 일정 관리 캘린더
 *
 * @type {React.ForwardRefExoticComponent<E3CalendarProps & React.RefAttributes}
 */
const E3Calendar = React.forwardRef(
  (
    {
      height = "100%",
      defaultToday = new Date(),
      onSelect = () => {},
      onTaskSelect = () => {},
      defaultTasks = [],
      onTaskChange = () => {},
      dayComponent,
    },
    ref
  ) => {
    const style = { height };

    const [today, setToday] = useState(defaultToday);
    const [tasks, setTasks] = useState(defaultTasks);

    const _getNow = useCallback(() => today, [today]);

    React.useImperativeHandle(ref, () => ({
      /**
       * 모든 일정 입력
       */
      setTasks: _setTasks,
      /**
       * 일정 추가
       */
      addTask: _addTask,
      /**
       * 모든 일정 가져오기
       */
      getTasks: _getTasks,
    }));

    /**
     * 일정 전체 초기화
     * private set tasks function
     * @param {CalendarTask[]} tasks
     */
    const _setTasks = useCallback(
      (tasks, cb) => {
        setTasks(tasks);
        onTaskChange(tasks);
        cb && cb(tasks);
      },
      [onTaskChange]
    );

    /**
     * 일정 추가
     * private add task function
     * @param {CalendarTask} task
     */
    const _addTask = useCallback(
      (task, cb) => {
        const arr = [...tasks, task];
        setTasks(arr);
        onTaskChange(arr, task);
        cb && cb(arr);
      },
      [tasks, onTaskChange]
    );

    /**
     * 일정 출력
     * @return {CalendarTask[]}
     */
    const _getTasks = useCallback(() => tasks, [tasks]);

    /**
     * 캘린더 날짜 선택 핸들러
     * @param {Object} e
     * @param {Date[]} e.slots - 선택된 슬롯 목록
     * @param {Date} e.start - 시작 날짜
     * @param {Date} e.end - 종료 날짜
     */
    const handleSelect = useCallback(
      (e) => {
        const selectDate = e.slots[0];
        const list = e.slots;
        const start = e.start;
        // end 날짜가 + 1일 되있어서 일관성을 위하여 1 감소
        const end = e.end;
        end.setDate(end.getDate() - 1);
        onSelect(selectDate, { list, start, end });
      },
      [onSelect]
    );

    const _componentOption = dayComponent
      ? {
          month: { dateHeader: dayComponent },
        }
      : undefined;

    return (
      <Calendar
        localizer={localizer}
        style={style}
        getNow={_getNow}
        onSelectSlot={handleSelect}
        events={tasks}
        onSelectEvent={(event, e) => onTaskSelect(event, e)}
        selectable={true}
        eventPropGetter={(event) => {
          const { color } = event;
          if (!color) return {};
          const cn = `cal-${color}`;
          return { className: cn };
        }}
        components={_componentOption}
      />
    );
  }
);

const E3CalendarPropTypes = {
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  defaultToday: PropTypes.object,
  onSelect: PropTypes.func,
  onTaskSelect: PropTypes.func,
  defaultTasks: PropTypes.array,
  onTaskChange: PropTypes.func,
};

E3Calendar.propTypes = E3CalendarPropTypes;
E3Calendar.displayName = "E3Calendar";

export default E3Calendar;
